import React, { Dispatch, SetStateAction, useState, useEffect } from 'react';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';

import FilterSelectInput from '../form/components/FilterSelectInput';
import { SelectOption } from '../form/model';

export interface FilterDefinition {
  label: string;
  type: 'select' | 'datePicker' | string;
  param: string;
  choices?: SelectOption[];
  width?: number;
  mutuallyExclusiveWith?: string[];
}

interface SingleFilterProps {
  filter: FilterDefinition;
  controller: string;
  fetchRecordsList: (
    controller: string,
    updatedFilterValues: Record<string, any>,
    page: number,
    searchQuery: string,
  ) => void;
  filterValues: Record<string, any>;
  setFilterValues: Dispatch<SetStateAction<Record<string, any>>>;
  searchQuery: string;
}

const SingleFilter: React.FC<SingleFilterProps> = (props) => {
  const { filter, controller, fetchRecordsList, filterValues, setFilterValues, searchQuery } = props;

  const [localStartDate, setLocalStartDate] = useState<string>(filterValues?.[`${filter.param}_gteq`] || '');
  const [localEndDate, setLocalEndDate] = useState<string>(filterValues?.[`${filter.param}_lteq`] || '');

  useEffect(() => {
    if (filter?.type === 'datePicker') {
      setLocalStartDate(filterValues?.[`${filter.param}_gteq`] || '');
      setLocalEndDate(filterValues?.[`${filter.param}_lteq`] || '');
    }
  }, [filterValues, filter?.param, filter?.type]);

  const handleFiltersChanged = (param: string, value: string) => {
    const updatedFilterValues = { ...filterValues };
    updatedFilterValues[param] = value;

    // Clear mutually exclusive filters if a value is being set (not 'all')
    if (filter.mutuallyExclusiveWith && value && value !== 'all') {
      filter.mutuallyExclusiveWith.forEach((exclusiveParam) => {
        delete updatedFilterValues[exclusiveParam];
      });
    }

    setFilterValues(updatedFilterValues);
    fetchRecordsList(controller, updatedFilterValues, 1, searchQuery);
  };

  const handleApplyDateFilter = () => {
    const updatedFilterValues = { ...filterValues };
    const startDateKey = `${filter.param}_gteq`;
    const endDateKey = `${filter.param}_lteq`;

    // Update or remove start date filter
    if (localStartDate) {
      updatedFilterValues[startDateKey] = localStartDate;
    } else {
      delete updatedFilterValues[startDateKey];
    }

    // Update or remove end date filter
    if (localEndDate) {
      updatedFilterValues[endDateKey] = localEndDate;
    } else {
      delete updatedFilterValues[endDateKey];
    }

    setFilterValues(updatedFilterValues);
    fetchRecordsList(controller, updatedFilterValues, 1, searchQuery);
  };

  const isDateFilterReady = localStartDate || localEndDate;

  const renderFilter = () => {
    switch (filter.type) {
      case 'text':
        return (
          <Grid item xs={12}>
            <TextField
              label={filter.label}
              variant="outlined"
              size="small"
              fullWidth
              value={filterValues?.[filter.param] || ''}
              onChange={(e) => {
                const updatedFilterValues = { ...filterValues, [filter.param]: e.target.value };
                setFilterValues(updatedFilterValues);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  fetchRecordsList(controller, filterValues, 1, searchQuery);
                }
              }}
            />
          </Grid>
        );

      case 'select':
        return (
          <Grid item xs={12}>
            <FilterSelectInput
              value={filterValues?.[filter.param]}
              defaultChoice={{ label: 'All', value: 'all' }}
              choices={filter.choices}
              valueChanged={(val) => handleFiltersChanged(filter.param, val)}
              label={filter.label}
            />
          </Grid>
        );

      case 'datePicker':
        return (
          <Box
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              backgroundColor: 'action.hover',
              paddingRight: 5,
              paddingTop: 2,
              paddingBottom: 2,
              paddingLeft: 2,
              m: 3,
            }}
          >
            <Grid container spacing={2} alignItems="flex-end">
              <Grid item xs={5}>
                <TextField
                  label="Start Date"
                  type="date"
                  variant="standard"
                  fullWidth
                  value={localStartDate}
                  onChange={(e) => setLocalStartDate(e.target.value)}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item xs={5}>
                <TextField
                  label="End Date"
                  type="date"
                  variant="standard"
                  fullWidth
                  value={localEndDate}
                  onChange={(e) => setLocalEndDate(e.target.value)}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item xs={2}>
                <Button variant="contained" onClick={handleApplyDateFilter} disabled={!isDateFilterReady} fullWidth>
                  Apply
                </Button>
              </Grid>
            </Grid>
          </Box>
        );

      default:
        console.warn(`Unknown filter type: ${filter.type}`);
        return null;
    }
  };

  return (
    <Grid container key={filter.param} spacing={filter.type === 'datePicker' ? 2 : 0}>
      {renderFilter()}
    </Grid>
  );
};

export default SingleFilter;
